import React, { useCallback, useEffect, useRef, useState } from "react";
import { SCHEDULE_ECOMM_MERGE_TAGS } from "./scheduleEcommMergeTags";
import { useRailFlyoutPosition } from "./useRailFlyoutPosition";

export default function ScheduleEcommMergeTagsPanel({
  tags = SCHEDULE_ECOMM_MERGE_TAGS,
  onInsertTag,
  onActivate = null,
  disabled = false,
  variant = "popover",
  compact = false,
  insertTargetLabel = null,
  toggleMode = "hover",
  placement = "inline",
  open: openProp,
  onOpenChange = null,
  flyout = "fixed",
}) {
  const [lastInserted, setLastInserted] = useState("");
  const [internalOpen, setInternalOpen] = useState(false);
  const rootRef = useRef(null);
  const clickToggle = toggleMode === "click";
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : internalOpen;
  const panelStyle = useRailFlyoutPosition(
    open && placement === "rail" && flyout === "fixed",
    rootRef,
  );

  const setOpen = useCallback(
    (next) => {
      if (controlled) onOpenChange?.(next);
      else setInternalOpen(next);
    },
    [controlled, onOpenChange],
  );

  const handleTag = useCallback(
    (tag) => {
      if (disabled || !onInsertTag) return;
      onInsertTag(tag);
      setLastInserted(tag);
      window.setTimeout(() => setLastInserted(""), 1500);
    },
    [disabled, onInsertTag],
  );

  useEffect(() => {
    if (!clickToggle || !open) return undefined;

    const onPointerDown = (event) => {
      if (event.target.closest(".ql-toolbar, .ql-picker, .ql-picker-options, .ql-editor")) {
        return;
      }
      const panel = document.querySelector(".gvisit-insert-fields__popover.is-visible");
      if (rootRef.current?.contains(event.target)) return;
      if (panel?.contains(event.target)) return;
      setOpen(false);
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("click", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("click", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [clickToggle, open, setOpen]);

  if (variant === "popover") {
    return (
      <div
        ref={rootRef}
        className={`gvisit-insert-fields${
          compact ? " gvisit-insert-fields--compact" : ""
        }${clickToggle ? " gvisit-insert-fields--click" : ""}${
          placement === "rail" ? " gvisit-insert-fields--rail" : ""
        }${open ? " is-open" : ""}`}
      >
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm gvisit-insert-fields__btn"
          disabled={disabled}
          aria-expanded={clickToggle ? open : undefined}
          onMouseDown={() => {
            if (!clickToggle) onActivate?.();
          }}
          onFocus={() => {
            if (!clickToggle) onActivate?.();
          }}
          onClick={() => {
            if (clickToggle) {
              onActivate?.();
              setOpen(!open);
            }
          }}
        >
          <i className="fas fa-tags me-1" aria-hidden />
          Insert fields
          <i
            className={`fas ${open ? "fa-chevron-up" : "fa-chevron-down"} ms-1 gvisit-insert-fields__caret`}
            aria-hidden
          />
        </button>
        {open ? (
          <div
            className="gvisit-insert-fields__popover gvisit-comm-rail-flyout is-visible"
            style={panelStyle || undefined}
            role="menu"
          >
            <p className="gvisit-insert-fields__popover-title">Replacement tags</p>
            {insertTargetLabel ? (
              <p className="gvisit-insert-fields__popover-target">
                Into: <span>{insertTargetLabel}</span>
              </p>
            ) : null}
            {tags.map((row) => (
              <button
                key={row.tag}
                type="button"
                className="gvisit-insert-fields__item"
                role="menuitem"
                disabled={disabled}
                onClick={() => handleTag(row.tag)}
              >
                <code>{row.tag}</code>
                <span>{row.description}</span>
              </button>
            ))}
          </div>
        ) : null}
        {lastInserted ? (
          <span className="gvisit-insert-fields__toast">
            <i className="fas fa-check" aria-hidden /> Inserted {lastInserted}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div className="gvisit-ecomm-tags" role="list" aria-label="Insert fields">
      <div className="gvisit-ecomm-tags__list">
        {tags.map((row) => (
          <button
            key={row.tag}
            type="button"
            className="gvisit-ecomm-tags__chip"
            disabled={disabled}
            onClick={() => handleTag(row.tag)}
          >
            <code>{row.tag}</code>
            <span className="gvisit-ecomm-tags__chip-desc">{row.description}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
